import { useRef } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { Button } from '@/ui';
import type { CriteriaPaneProps } from './types';
import { SectionBody, SectionSlot } from './Panes';
import {
  availableSections,
  availableTabs,
  isSectionConfigured,
  type CriteriaTabKey,
  type SectionContext,
} from './sections';

interface SummaryPaneProps extends CriteriaPaneProps {
  context: SectionContext;
  onOpenTab: (tab: CriteriaTabKey) => void;
  /** Sections the criteria search added; shown even while they hold nothing. */
  pinnedIds: ReadonlySet<string>;
  onUnpin: (sectionId: string) => void;
  highlightId?: string | null;
}

/**
 * Everything that is configured, editable in place. It renders the very same section
 * components as the tabs — there is no separate read-only view to keep in step — and hides
 * the sections that hold nothing.
 *
 * A section shown once stays for as long as the Summary is open: clearing its last value
 * would otherwise make the section vanish from under the cursor mid-edit. Sections pinned
 * from the criteria search show too, so a criterion can be set up from here.
 */
export function SummaryPane({
  context,
  onOpenTab,
  pinnedIds,
  onUnpin,
  highlightId = null,
  ...paneProps
}: SummaryPaneProps) {
  const { state } = paneProps;
  const shownRef = useRef<Set<string>>(new Set());
  const sections = availableSections(context);
  const tabs = availableTabs(context);
  const section = (id: string) => sections.find((s) => s.id === id)!;

  const visible = sections.filter((section) => {
    if (pinnedIds.has(section.id) || isSectionConfigured(section, state, context)) shownRef.current.add(section.id);
    return shownRef.current.has(section.id);
  });
  const configuredCount = sections.filter((section) => isSectionConfigured(section, state, context)).length;

  if (visible.length === 0) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: 'center',
          border: '2px dashed',
          borderColor: 'divider',
          borderRadius: 1,
          // The card body is the inset well already; the empty state is a paper card on it.
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          No criteria configured yet. Search for one above, or pick a tab.
        </Typography>
        <Stack direction='row' spacing={1} useFlexGap sx={{ justifyContent: 'center', flexWrap: 'wrap' }}>
          {tabs.map((tab) => (
            <Button key={tab.key} color='outline-primary' onClick={() => onOpenTab(tab.key)}>
              {tab.title}
            </Button>
          ))}
        </Stack>
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      <Typography variant='body2' color='text.secondary'>
        {configuredCount > 0
          ? `${configuredCount} section${configuredCount === 1 ? '' : 's'} configured. Edit them here, or open a tab for everything else.`
          : 'Nothing configured yet. Set up the sections you added below.'}
      </Typography>

      {tabs.map((tab) => {
        const tabSections = visible.filter((section) => section.tab === tab.key);
        if (tabSections.length === 0) return null;
        return (
          <Box key={tab.key} component='section' aria-label={`${tab.title} criteria`}>
            <Stack direction='row' spacing={1} useFlexGap sx={{ alignItems: 'center', mb: 1 }}>
              <Typography
                component='h3'
                variant='overline'
                sx={{ fontWeight: 600, letterSpacing: '0.06em', lineHeight: 1.5, color: 'text.secondary' }}
              >
                {tab.title}
              </Typography>
              <Chip size='small' label={tabSections.length} />
              <Button
                color='link'
                endIcon={<ArrowForward fontSize='inherit' />}
                onClick={() => onOpenTab(tab.key)}
                sx={{ ml: 'auto' }}
              >
                Open tab
              </Button>
            </Stack>
            <Stack spacing={2}>
              {tabSections.map(({ id }) => (
                <Box key={id}>
                  {pinnedIds.has(id) && !isSectionConfigured(section(id), state, context) && (
                    <Stack direction='row' spacing={0.5} sx={{ alignItems: 'center', mb: 0.5 }}>
                      <Typography variant='caption' color='text.secondary'>
                        Added from search
                      </Typography>
                      <Button
                        color='link'
                        size='sm'
                        onClick={() => {
                          shownRef.current.delete(id);
                          onUnpin(id);
                        }}
                      >
                        Remove
                      </Button>
                    </Stack>
                  )}
                  <SectionSlot id={id} highlighted={highlightId === id}>
                    <SectionBody section={section(id)} paneProps={paneProps} />
                  </SectionSlot>
                </Box>
              ))}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
}
